import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  SelectChangeEvent,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Help as HelpIcon,
  NewReleases as NewReleasesIcon,
  Warning as WarningIcon,
  Business as BusinessIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { format, subDays } from 'date-fns';
import MainLayout from '../components/MainLayout';
import {
  CallInsightsStats,
  getBuyingSignalColor,
  getCoveragePctClasses,
} from '../components/adherence/types';
import { apiService } from '../services/apiService';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { useTutorial } from '../hooks/useTutorial';
import { useAppSelector } from '../hooks/redux';
import NewInquiriesModal from '../components/NewInquiriesModal';
import CommonObjectionsModal from '../components/CommonObjectionsModal';
import CommonFAQsModal from '../components/CommonFAQsModal';
import CommonLandminesModal from '../components/CommonLandminesModal';
import CommonCompetitorsModal from '../components/CommonCompetitorsModal';
import HardestItemsModal from '../components/dashboard/HardestItemsModal';

interface AnalyticsData {
  totalSessions: number;
  totalDuration: number;
  avgDuration: number;
  totalTranscripts: number;
  totalObjections: number;
  totalFAQs: number;
  totalNewInquiries: number;
  totalUnprocessedInquiries: number;
  totalLandmineDetections: number;
  totalCompetitorMentions: number;
  uniqueUserCount: number;
}

interface UserInfo {
  userId: string;
  userEmail: string;
  name?: string;
}

interface Session {
  _id: string;
  sessionId: string;
  sessionName: string;
  userEmail: string;
  callStartTime: string;
  callDuration: number;
  statistics: {
    totalObjections: number;
    totalFAQs: number;
    totalNewInquiries: number;
    totalLandmineDetections?: number;
    totalCompetitorMentions?: number;
  };
}

interface InquiryData {
  sessionId: string;
  sessionName: string;
  userEmail: string;
  userId: string;
  callStartTime: string;
  inquiry: {
    question: string;
    answer?: string;
    category: string;
    timestamp: string;
    relevance_score: number;
    isProcessed: boolean;
  };
}

interface ObjectionDetection {
  question: string;
  sessionId: string;
}

interface CommonObjectionData {
  groupId: string;
  sourceId: string | null;
  parentId: string | null;
  originalQuestion: string | null;
  originalAnswer: string | null;
  detections: ObjectionDetection[];
  totalCount: number;
  category: string;
  storedAnswer: string | null;
}

interface FAQDetection {
  question: string;
  sessionId: string;
}

interface CommonFAQData {
  groupId: string;
  sourceId: string | null;
  parentId: string | null;
  originalQuestion: string | null;
  originalAnswer: string | null;
  detections: FAQDetection[];
  totalCount: number;
  category: string;
  storedAnswer: string | null;
}

interface LandmineDetection {
  matchedText: string;
  sessionId: string;
  speaker?: string;
}

interface CommonLandmineData {
  sourceId: string | null;
  trigger: string;
  triggerVariations: string[];
  detections: LandmineDetection[];
  totalCount: number;
  category: string;
  priority: 'high' | 'medium' | 'low';
}

interface CompetitorDetection {
  matchedText: string;
  sessionId: string;
  speaker?: string;
}

interface CommonCompetitorData {
  sourceId: string | null;
  name: string;
  variations: string[];
  detections: CompetitorDetection[];
  totalCount: number;
  category: string;
  avgConfidence: number;
}

type TimeFilter = '7days' | '30days' | 'all';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, isSuperAdmin } = useRoleAccess();
  const { mongoUser, tutorialActiveStepIndex } = useAppSelector((state) => state.auth);
  const { isTutorialCompleted, startTutorial, isRunning: isTutorialRunning } = useTutorial();

  // Track if we've already checked for first-time tutorial
  const hasCheckedTutorial = useRef(false);

  // Store startTutorial in a ref to avoid useEffect re-running when function changes
  const startTutorialRef = useRef(startTutorial);
  startTutorialRef.current = startTutorial;

  // State
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('30days');
  const [selectedUserId, setSelectedUserId] = useState<string>('all');
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [recentSessions, setRecentSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New Inquiries Modal State
  const [showInquiriesModal, setShowInquiriesModal] = useState(false);
  const [inquiries, setInquiries] = useState<InquiryData[]>([]);
  const [loadingInquiries, setLoadingInquiries] = useState(false);

  // Common Objections Modal State
  const [showObjectionsModal, setShowObjectionsModal] = useState(false);
  const [commonObjections, setCommonObjections] = useState<CommonObjectionData[]>([]);
  const [loadingObjections, setLoadingObjections] = useState(false);

  // Common FAQs Modal State
  const [showFAQsModal, setShowFAQsModal] = useState(false);
  const [commonFAQs, setCommonFAQs] = useState<CommonFAQData[]>([]);
  const [loadingFAQs, setLoadingFAQs] = useState(false);

  // Common Landmines Modal State
  const [showLandminesModal, setShowLandminesModal] = useState(false);
  const [commonLandmines, setCommonLandmines] = useState<CommonLandmineData[]>([]);
  const [loadingLandmines, setLoadingLandmines] = useState(false);

  // Common Competitors Modal State
  const [showCompetitorsModal, setShowCompetitorsModal] = useState(false);
  const [commonCompetitors, setCommonCompetitors] = useState<CommonCompetitorData[]>([]);
  const [loadingCompetitors, setLoadingCompetitors] = useState(false);

  // Hardest Items Modals State
  const [showHardestObjectionsModal, setShowHardestObjectionsModal] = useState(false);
  const [showHardestFAQsModal, setShowHardestFAQsModal] = useState(false);
  const [showHardestLandminesModal, setShowHardestLandminesModal] = useState(false);
  const [showHardestCompetitorsModal, setShowHardestCompetitorsModal] = useState(false);

  // Team Coaching Priorities
  const [callInsightsStats, setCallInsightsStats] = useState<CallInsightsStats | null>(null);

  useEffect(() => {
    fetchAnalytics();
    fetchRecentSessions();
    fetchCallInsightsStats();
    if (isAdmin) {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeFilter, selectedUserId, isAdmin]);

  // Auto-start tutorial for first-time users
  useEffect(() => {
    console.log('[Tutorial Debug]', {
      hasChecked: hasCheckedTutorial.current,
      mongoUser: !!mongoUser,
      isTutorialCompleted,
      isTutorialRunning,
      tutorialActiveStepIndex,
      tutorialCompletedValue: mongoUser?.preferences?.tutorialCompleted,
    });

    // Don't auto-start if tutorial is already running or has an active step (navigating between pages)
    if (isTutorialRunning || tutorialActiveStepIndex !== null) {
      console.log('[Tutorial Debug] Tutorial already in progress, skipping auto-start');
      return;
    }

    // Wait for mongoUser to be loaded before checking tutorial status
    if (!hasCheckedTutorial.current && mongoUser && !isTutorialCompleted) {
      console.log('[Tutorial Debug] Will start tutorial in 1 second...');
      hasCheckedTutorial.current = true;
      // Small delay to ensure sidebar is rendered
      // Note: No cleanup function - hasCheckedTutorial.current ensures this only runs once
      setTimeout(() => {
        console.log('[Tutorial Debug] setTimeout fired, calling startTutorial now');
        startTutorialRef.current();
      }, 1000);
    }
  }, [mongoUser, isTutorialCompleted, isTutorialRunning, tutorialActiveStepIndex]);

  const getDateRange = () => {
    const now = new Date();
    let dateFrom: string | undefined;

    switch (timeFilter) {
      case '7days':
        dateFrom = subDays(now, 7).toISOString();
        break;
      case '30days':
        dateFrom = subDays(now, 30).toISOString();
        break;
      case 'all':
      default:
        dateFrom = undefined;
    }

    return { dateFrom, dateTo: now.toISOString() };
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const { dateFrom, dateTo } = getDateRange();
      const params: Record<string, string> = {};

      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (isAdmin && selectedUserId !== 'all') {
        params.userId = selectedUserId;
      }

      const response = await apiService.get('/admin/statistics/analytics', params);

      if (response.success && response.data) {
        setAnalytics(response.data);
      }
    } catch (err: any) {
      console.error('Failed to fetch analytics:', err);
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchCallInsightsStats = async () => {
    try {
      const { dateFrom, dateTo } = getDateRange();
      const params: Record<string, string> = {};

      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (isAdmin && selectedUserId !== 'all') {
        params.userId = selectedUserId;
      }

      const response = await apiService.get('/admin/statistics/call-insights-quality', params);
      if (response.success && response.data) {
        setCallInsightsStats(response.data);
      }
    } catch (err: any) {
      console.error('Failed to fetch call insights stats:', err);
    }
  };

  // Populate the selector from ALL org users (any role, active or not) — not
  // just those who happen to have sessions — since admins also run sales calls.
  const fetchUsers = async () => {
    try {
      const response: any = await apiService.get('/admin/users', { limit: '500' });
      const list: any[] = response?.data?.users ?? response?.data ?? [];
      const mapped: UserInfo[] = list
        .filter((u) => u && (u.firebaseUid || u.uid) && u.email)
        .map((u) => ({
          userId: u.firebaseUid || u.uid,
          userEmail: u.email,
          name: u.name,
        }))
        .sort((a, b) =>
          (a.name || a.userEmail).localeCompare(b.name || b.userEmail, undefined, {
            sensitivity: 'base',
          })
        );
      setUsers(mapped);
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchRecentSessions = async () => {
    try {
      const { dateFrom, dateTo } = getDateRange();
      const params = new URLSearchParams({
        page: '1',
        limit: '5',
        sortBy: 'callStartTime',
        sortOrder: 'desc',
      });

      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);
      if (isAdmin && selectedUserId !== 'all') {
        params.append('userId', selectedUserId);
      }

      const response = await apiService.get(`/admin/sessions?${params}`);

      if (response.success && response.data) {
        setRecentSessions(response.data);
      }
    } catch (err: any) {
      console.error('Failed to fetch recent sessions:', err);
    }
  };

  const fetchInquiries = async () => {
    try {
      setLoadingInquiries(true);
      const { dateFrom, dateTo } = getDateRange();
      const params: Record<string, string> = {};

      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (isAdmin && selectedUserId !== 'all') {
        params.userId = selectedUserId;
      }

      const response = await apiService.get('/admin/inquiries/new', params);

      if (response.success && response.data) {
        setInquiries(response.data.inquiries || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch inquiries:', err);
      setError('Failed to load inquiries');
    } finally {
      setLoadingInquiries(false);
    }
  };

  const handleNewInquiriesClick = async () => {
    setShowInquiriesModal(true);
    await fetchInquiries();
  };

  const fetchCommonObjections = async () => {
    try {
      setLoadingObjections(true);
      const { dateFrom, dateTo } = getDateRange();
      const params: Record<string, string> = {};

      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (isAdmin && selectedUserId !== 'all') {
        params.userId = selectedUserId;
      }

      const response = await apiService.get('/admin/statistics/common-objections', params);

      if (response.success && response.data) {
        setCommonObjections(response.data || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch common objections:', err);
    } finally {
      setLoadingObjections(false);
    }
  };

  const fetchCommonFAQs = async () => {
    try {
      setLoadingFAQs(true);
      const { dateFrom, dateTo } = getDateRange();
      const params: Record<string, string> = {};

      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (isAdmin && selectedUserId !== 'all') {
        params.userId = selectedUserId;
      }

      const response = await apiService.get('/admin/statistics/common-faqs', params);

      if (response.success && response.data) {
        setCommonFAQs(response.data || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch common FAQs:', err);
    } finally {
      setLoadingFAQs(false);
    }
  };

  const handleObjectionsClick = async () => {
    setShowObjectionsModal(true);
    await fetchCommonObjections();
  };

  const handleFAQsClick = async () => {
    setShowFAQsModal(true);
    await fetchCommonFAQs();
  };

  const fetchCommonLandmines = async () => {
    try {
      setLoadingLandmines(true);
      const { dateFrom, dateTo } = getDateRange();
      const params: Record<string, string> = {};

      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (isAdmin && selectedUserId !== 'all') {
        params.userId = selectedUserId;
      }

      const response = await apiService.get('/admin/statistics/common-landmines', params);

      if (response.success && response.data) {
        setCommonLandmines(response.data || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch common landmines:', err);
    } finally {
      setLoadingLandmines(false);
    }
  };

  const fetchCommonCompetitors = async () => {
    try {
      setLoadingCompetitors(true);
      const { dateFrom, dateTo } = getDateRange();
      const params: Record<string, string> = {};

      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (isAdmin && selectedUserId !== 'all') {
        params.userId = selectedUserId;
      }

      const response = await apiService.get('/admin/statistics/common-competitors', params);

      if (response.success && response.data) {
        setCommonCompetitors(response.data || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch common competitors:', err);
    } finally {
      setLoadingCompetitors(false);
    }
  };

  const handleLandminesClick = async () => {
    setShowLandminesModal(true);
    await fetchCommonLandmines();
  };

  const handleCompetitorsClick = async () => {
    setShowCompetitorsModal(true);
    await fetchCommonCompetitors();
  };

  const handleInquiryProcessed = () => {
    // Refresh analytics to update the count
    fetchAnalytics();
    // Refresh inquiries list
    fetchInquiries();
  };

  const handleTimeFilterChange = (filter: TimeFilter) => {
    setTimeFilter(filter);
  };

  const handleUserChange = (event: SelectChangeEvent<string>) => {
    setSelectedUserId(event.target.value);
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const viewSessionDetails = (sessionId: string) => {
    navigate(`/sessions/${sessionId}`);
  };

  const viewAllSessions = () => {
    navigate('/sessions');
  };

  // Prepare chart data
  const chartData = analytics ? [
    { name: 'Objections', value: analytics.totalObjections, color: '#f59e0b' },
    { name: 'FAQs', value: analytics.totalFAQs, color: '#3b82f6' },
    { name: 'New Objection/FAQ', value: analytics.totalNewInquiries, color: '#10b981' },
    { name: 'Landmines', value: analytics.totalLandmineDetections, color: '#ef4444' },
    { name: 'Competitors', value: analytics.totalCompetitorMentions, color: '#8b5cf6' },
  ] : [];

  const metricCards = analytics ? [
    {
      title: 'Total Sessions',
      value: analytics.totalSessions,
      icon: <AccessTimeIcon fontSize="large" />,
      color: '#3b82f6',
      bgColor: 'bg-blue-50',
      onClick: undefined,
    },
    {
      title: 'Objections Detected',
      value: analytics.totalObjections,
      icon: <QuestionAnswerIcon fontSize="large" />,
      color: '#f59e0b',
      bgColor: 'bg-amber-50',
      onClick: handleObjectionsClick,
    },
    {
      title: 'FAQs Answered',
      value: analytics.totalFAQs,
      icon: <HelpIcon fontSize="large" />,
      color: '#3b82f6',
      bgColor: 'bg-blue-50',
      onClick: handleFAQsClick,
    },
    {
      title: 'New Objection/FAQ',
      value: analytics.totalNewInquiries,
      icon: <NewReleasesIcon fontSize="large" />,
      color: '#10b981',
      bgColor: 'bg-green-50',
      onClick: handleNewInquiriesClick,
    },
    {
      title: 'Landmine Detections',
      value: analytics.totalLandmineDetections,
      icon: <WarningIcon fontSize="large" />,
      color: '#ef4444',
      bgColor: 'bg-red-50',
      onClick: handleLandminesClick,
    },
    {
      title: 'Competitor Mentions',
      value: analytics.totalCompetitorMentions,
      icon: <BusinessIcon fontSize="large" />,
      color: '#8b5cf6',
      bgColor: 'bg-purple-50',
      onClick: handleCompetitorsClick,
    },
  ] : [];

  // Super admins have no access to dashboard — redirect them to knowledge base
  if (isSuperAdmin) {
    return <Navigate to="/knowledge-base" replace />;
  }

  return (
    <MainLayout title="Dashboard">
      <div className="p-2">
        {/* Header */}
        <div className="mb-3">
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600 mt-1">
            {isAdmin
              ? selectedUserId === 'all'
                ? 'Organization-wide analytics overview'
                : `Analytics for ${users.find(u => u.userId === selectedUserId)?.userEmail || 'selected user'}`
              : 'Your personal analytics overview'}
          </p>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              {/* Timeline Filter */}
              <div className="flex gap-2">
                <Button
                  variant={timeFilter === '7days' ? 'contained' : 'outlined'}
                  onClick={() => handleTimeFilterChange('7days')}
                  size="small"
                >
                  Last 7 Days
                </Button>
                <Button
                  variant={timeFilter === '30days' ? 'contained' : 'outlined'}
                  onClick={() => handleTimeFilterChange('30days')}
                  size="small"
                >
                  Last 30 Days
                </Button>
                <Button
                  variant={timeFilter === 'all' ? 'contained' : 'outlined'}
                  onClick={() => handleTimeFilterChange('all')}
                  size="small"
                >
                  All Time
                </Button>
              </div>

              {/* Admin User Dropdown */}
              {isAdmin && (
                <FormControl size="small" sx={{ minWidth: 250 }}>
                  <InputLabel>View Analytics For</InputLabel>
                  <Select
                    value={selectedUserId}
                    label="View Analytics For"
                    onChange={handleUserChange}
                  >
                    <MenuItem value="all">Across Organization</MenuItem>
                    {users.map((user) => (
                      <MenuItem key={user.userId} value={user.userId}>
                        {user.name ? `${user.name} (${user.userEmail})` : user.userEmail}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Error Display */}
        {error && (
          <Alert severity="error" className="mb-6">
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && !analytics ? (
          <div className="flex justify-center items-center py-12">
            <CircularProgress />
          </div>
        ) : analytics ? (
          <div className="relative">
            {/* Loading Overlay - shows when refetching data */}
            {loading && (
              <div className="absolute inset-0 bg-white bg-opacity-75 z-10 flex items-center justify-center rounded-lg">
                <div className="flex flex-col items-center">
                  <CircularProgress />
                  <Typography variant="body2" color="textSecondary" className="mt-2">
                    Updating analytics...
                  </Typography>
                </div>
              </div>
            )}

            {/* Metric Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-6">
              {metricCards.map((card, index) => {
                const isNewInquiriesCard = card.title === 'New Objection/FAQ';
                const isTotalSessionsCard = card.title === 'Total Sessions';

                // Determine hardest button config for the four scored cards
                let hardestLabel: string | null = null;
                let hardestOnClick: (() => void) | null = null;
                const hasHardestData = callInsightsStats != null;

                if (card.title === 'Objections Detected') {
                  hardestLabel = 'Hardest Objections';
                  hardestOnClick = () => setShowHardestObjectionsModal(true);
                } else if (card.title === 'FAQs Answered') {
                  hardestLabel = 'Hardest FAQs';
                  hardestOnClick = () => setShowHardestFAQsModal(true);
                } else if (card.title === 'Landmine Detections') {
                  hardestLabel = 'Hardest Landmines';
                  hardestOnClick = () => setShowHardestLandminesModal(true);
                } else if (card.title === 'Competitor Mentions') {
                  hardestLabel = 'Hardest Competitors';
                  hardestOnClick = () => setShowHardestCompetitorsModal(true);
                }

                const showDualButtons = hardestLabel && hardestOnClick;

                return (
                  <Card
                    key={index}
                    className={`h-full ${card.bgColor}`}
                  >
                    <CardContent>
                      <Box className="flex items-start justify-between">
                        <div className="flex-1">
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            {card.title}
                          </Typography>
                          <Typography variant="h4" component="div" className="font-bold">
                            {card.value.toLocaleString()}
                          </Typography>
                          {isNewInquiriesCard && analytics && (
                            <Typography variant="caption" color="textSecondary" className="mt-1 block">
                              {analytics.totalUnprocessedInquiries} unprocessed
                            </Typography>
                          )}
                        </div>
                        <div style={{ color: card.color }}>
                          {card.icon}
                        </div>
                      </Box>

                      {/* Action buttons */}
                      {!isTotalSessionsCard && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {card.onClick && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                card.onClick?.();
                              }}
                              className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors font-medium"
                            >
                              View All
                            </button>
                          )}
                          {showDualButtons && hasHardestData && isAdmin && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                hardestOnClick?.();
                              }}
                              className="text-xs px-3 py-1.5 rounded border font-medium transition-colors"
                              style={{ borderColor: card.color, color: card.color, backgroundColor: 'white' }}
                            >
                              {hardestLabel}
                            </button>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Team Coaching Priorities — admin only */}
            {isAdmin && callInsightsStats && (
              callInsightsStats.closing_signals.length > 0 ||
              callInsightsStats.playbook_coverage !== null
            ) && (
              <div className="mb-6">
                <Typography variant="h6" className="mb-4 font-semibold text-gray-900">
                  Team Coaching Priorities
                </Typography>

                {/* 2-column layout: Closing Signal + Playbook Coverage */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Closing Signal Breakdown */}
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle2" className="font-semibold text-gray-700 mb-3">
                        Closing Signal Breakdown
                      </Typography>
                      {callInsightsStats.closing_signals.length === 0 ? (
                        <p className="text-xs text-gray-400">No closing signal data yet</p>
                      ) : (() => {
                        const totalSignalSessions = callInsightsStats.closing_signals.reduce(
                          (sum, s) => sum + s.count,
                          0
                        );
                        return (
                          <div>
                            {/* Stacked pill bar */}
                            <div className="flex h-4 rounded-full overflow-hidden mb-3">
                              {callInsightsStats.closing_signals.map((sig, idx) => {
                                const pct = totalSignalSessions > 0 ? (sig.count / totalSignalSessions) * 100 : 0;
                                const bgClass = getBuyingSignalColor(sig._id ?? '')
                                  .split(' ')
                                  .find((c) => c.startsWith('bg-')) ?? 'bg-gray-300';
                                return (
                                  <div
                                    key={idx}
                                    className={bgClass}
                                    style={{ width: `${pct}%` }}
                                    title={`${sig._id}: ${sig.count}`}
                                  />
                                );
                              })}
                            </div>
                            {/* Legend */}
                            <div className="space-y-1">
                              {callInsightsStats.closing_signals.map((sig, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                  <span
                                    className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${
                                      getBuyingSignalColor(sig._id ?? '')
                                        .split(' ')
                                        .find((c) => c.startsWith('bg-')) ?? 'bg-gray-300'
                                    }`}
                                  />
                                  <span className="text-xs text-gray-600 capitalize">
                                    {(sig._id ?? 'Unknown').replace(/_/g, ' ')}: {sig.count} call
                                    {sig.count !== 1 ? 's' : ''}
                                  </span>
                                </div>
                              ))}
                            </div>
                            <p className="text-xs text-gray-400 mt-2">
                              Based on {totalSignalSessions} session{totalSignalSessions !== 1 ? 's' : ''}
                              {callInsightsStats.total_analyzed_sessions > 0 && ` (${callInsightsStats.total_analyzed_sessions} analyzed total)`}
                            </p>
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>

                  {/* Playbook Coverage */}
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle2" className="font-semibold text-gray-700 mb-3">
                        Playbook Coverage
                      </Typography>
                      {!callInsightsStats.playbook_coverage ? (
                        <p className="text-xs text-gray-400">No playbook sessions yet</p>
                      ) : (
                        <div className="space-y-3">
                          {[
                            { label: 'Intro', pct: callInsightsStats.playbook_coverage.avg_intro },
                            { label: 'Discovery Questions', pct: callInsightsStats.playbook_coverage.avg_questions },
                            { label: 'Deal Progression', pct: callInsightsStats.playbook_coverage.avg_deal_progression },
                          ].map(({ label, pct }) => {
                            const rounded = Math.round(pct ?? 0);
                            return (
                              <div key={label}>
                                <div className="flex justify-between text-xs text-gray-600 mb-1">
                                  <span>{label}</span>
                                  <span className="font-semibold">{rounded}%</span>
                                </div>
                                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${getCoveragePctClasses(rounded)}`}
                                    style={{ width: `${rounded}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                          <p className="text-xs text-gray-400 mt-2">
                            Based on {callInsightsStats.playbook_coverage.session_count} session
                            {callInsightsStats.playbook_coverage.session_count !== 1 ? 's' : ''}
                            {callInsightsStats.total_analyzed_sessions > 0 && ` (${callInsightsStats.total_analyzed_sessions} analyzed total)`}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Additional Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Session Statistics
                  </Typography>
                  <div className="space-y-2 mt-4">
                    <div className="flex justify-between items-center">
                      <Typography variant="body2" color="textSecondary">
                        Total Duration
                      </Typography>
                      <Typography variant="body1" className="font-semibold">
                        {formatDuration(analytics.totalDuration)}
                      </Typography>
                    </div>
                    <div className="flex justify-between items-center">
                      <Typography variant="body2" color="textSecondary">
                        Average Duration
                      </Typography>
                      <Typography variant="body1" className="font-semibold">
                        {formatDuration(analytics.avgDuration)}
                      </Typography>
                    </div>
                    <div className="flex justify-between items-center">
                      <Typography variant="body2" color="textSecondary">
                        Total Transcripts
                      </Typography>
                      <Typography variant="body1" className="font-semibold">
                        {analytics.totalTranscripts.toLocaleString()}
                      </Typography>
                    </div>
                    {isAdmin && selectedUserId === 'all' && (
                      <div className="flex justify-between items-center">
                        <Typography variant="body2" color="textSecondary">
                          Active Users
                        </Typography>
                        <Typography variant="body1" className="font-semibold">
                          {analytics.uniqueUserCount}
                        </Typography>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Chart */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Detection Overview
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={chartData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <defs>
                        <linearGradient id="colorObjections" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.3}/>
                        </linearGradient>
                        <linearGradient id="colorFAQs" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.3}/>
                        </linearGradient>
                        <linearGradient id="colorInquiries" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0.3}/>
                        </linearGradient>
                        <linearGradient id="colorLandmines" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#ef4444" stopOpacity={0.3}/>
                        </linearGradient>
                        <linearGradient id="colorCompetitors" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="name"
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                        axisLine={{ stroke: '#d1d5db' }}
                      />
                      <YAxis
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                        axisLine={{ stroke: '#d1d5db' }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        }}
                        cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                      />
                      <Bar
                        dataKey="value"
                        radius={[8, 8, 0, 0]}
                        maxBarSize={60}
                      >
                        {chartData.map((entry, index) => {
                          const gradientId = entry.name === 'Objections' ? 'colorObjections' :
                                           entry.name === 'FAQs' ? 'colorFAQs' :
                                           entry.name === 'New Objection/FAQ' ? 'colorInquiries' :
                                           entry.name === 'Landmines' ? 'colorLandmines' :
                                           'colorCompetitors';
                          return <Cell key={`cell-${index}`} fill={`url(#${gradientId})`} />;
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Recent Sessions */}
            <Card>
              <CardContent>
                <Box className="flex justify-between items-center mb-4">
                  <Typography variant="h6">
                    Recent Sessions
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={viewAllSessions}
                    endIcon={<TrendingUpIcon />}
                  >
                    View All Sessions
                  </Button>
                </Box>

                {recentSessions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No sessions found for the selected time period
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Session
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            User
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Duration
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Detections
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {recentSessions.map((session) => (
                          <tr
                            key={session._id}
                            onClick={() => viewSessionDetails(session.sessionId)}
                            className="hover:bg-blue-50 cursor-pointer transition-colors"
                          >
                            <td className="px-4 py-3">
                              <Typography variant="body2" className="font-medium">
                                {session.sessionName}
                              </Typography>
                            </td>
                            <td className="px-4 py-3">
                              <Typography variant="body2" color="textSecondary">
                                {session.userEmail}
                              </Typography>
                            </td>
                            <td className="px-4 py-3">
                              <Typography variant="body2">
                                {format(new Date(session.callStartTime), 'MMM dd, yyyy HH:mm')}
                              </Typography>
                            </td>
                            <td className="px-4 py-3">
                              <Typography variant="body2">
                                {formatDuration(session.callDuration)}
                              </Typography>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-xs space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-amber-600">❓</span>
                                  <span>{session.statistics?.totalObjections || 0}</span>
                                  <span className="text-blue-600">📚</span>
                                  <span>{session.statistics?.totalFAQs || 0}</span>
                                  <span className="text-green-600">📝</span>
                                  <span>{session.statistics?.totalNewInquiries || 0}</span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : null}

        {/* New Inquiries Modal */}
        <NewInquiriesModal
          isOpen={showInquiriesModal}
          onClose={() => setShowInquiriesModal(false)}
          inquiries={inquiries}
          loading={loadingInquiries}
          onInquiryProcessed={handleInquiryProcessed}
        />

        {/* Common Objections Modal */}
        <CommonObjectionsModal
          isOpen={showObjectionsModal}
          onClose={() => setShowObjectionsModal(false)}
          objections={commonObjections}
          loading={loadingObjections}
        />

        {/* Common FAQs Modal */}
        <CommonFAQsModal
          isOpen={showFAQsModal}
          onClose={() => setShowFAQsModal(false)}
          faqs={commonFAQs}
          loading={loadingFAQs}
        />

        {/* Common Landmines Modal */}
        <CommonLandminesModal
          isOpen={showLandminesModal}
          onClose={() => setShowLandminesModal(false)}
          landmines={commonLandmines}
          loading={loadingLandmines}
        />

        {/* Common Competitors Modal */}
        <CommonCompetitorsModal
          isOpen={showCompetitorsModal}
          onClose={() => setShowCompetitorsModal(false)}
          competitors={commonCompetitors}
          loading={loadingCompetitors}
        />

        {/* Hardest Objections Modal */}
        {callInsightsStats && (
          <HardestItemsModal
            isOpen={showHardestObjectionsModal}
            onClose={() => setShowHardestObjectionsModal(false)}
            title="Hardest Objections"
            columnHeader="Objection"
            items={callInsightsStats.weakest_objections}
            itemType="objection_faq"
            dateFrom={getDateRange().dateFrom}
            dateTo={getDateRange().dateTo}
            userId={isAdmin && selectedUserId !== 'all' ? selectedUserId : undefined}
          />
        )}

        {/* Hardest FAQs Modal */}
        {callInsightsStats && (
          <HardestItemsModal
            isOpen={showHardestFAQsModal}
            onClose={() => setShowHardestFAQsModal(false)}
            title="Hardest FAQs"
            columnHeader="FAQ"
            items={callInsightsStats.weakest_faqs}
            itemType="objection_faq"
            dateFrom={getDateRange().dateFrom}
            dateTo={getDateRange().dateTo}
            userId={isAdmin && selectedUserId !== 'all' ? selectedUserId : undefined}
          />
        )}

        {/* Hardest Landmines Modal */}
        {callInsightsStats && (
          <HardestItemsModal
            isOpen={showHardestLandminesModal}
            onClose={() => setShowHardestLandminesModal(false)}
            title="Hardest Landmines"
            columnHeader="Landmine"
            items={callInsightsStats.hardest_landmines}
            itemType="landmine"
            dateFrom={getDateRange().dateFrom}
            dateTo={getDateRange().dateTo}
            userId={isAdmin && selectedUserId !== 'all' ? selectedUserId : undefined}
          />
        )}

        {/* Hardest Competitors Modal */}
        {callInsightsStats && (
          <HardestItemsModal
            isOpen={showHardestCompetitorsModal}
            onClose={() => setShowHardestCompetitorsModal(false)}
            title="Hardest Competitors"
            columnHeader="Competitor"
            items={callInsightsStats.hardest_competitors}
            itemType="competitor"
            dateFrom={getDateRange().dateFrom}
            dateTo={getDateRange().dateTo}
            userId={isAdmin && selectedUserId !== 'all' ? selectedUserId : undefined}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default Dashboard;
